import { useState } from 'react';

import { useFuzzySearch } from './useFuzzySearch';
import axios from '@helpers/axios';
import type { ErrorMessage, SuccessMessage } from '@theme/theme';
import type { FirebaseUserSearchResult } from '../features/Admin/admin.types';
import { strogging } from '@shd/jslib/infra';

export const useAdminUserSearch = () => {
  const endpoint = '/api/firebase/user_search';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { debouncing, error, loading, onClearResults, results, search } =
    useFuzzySearch<FirebaseUserSearchResult[] | []>(endpoint);

  const onSearch = async (query: string) => {
    await search({
      searchBy: 'id',
      searchValue: query,
    });
  };

  return {
    debouncing,
    error,
    loading,
    onClearResults,
    onSearch,
    results,
  };
};

export const useAcceptTos = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorMessage | null>();
  const [success, setSuccess] = useState<SuccessMessage | null>();
  const acceptTos = async (userId: string) => {
    try {
      setLoading(true);
      const response = await axios.put(`/api/admin/user/${userId}/accept_tos`);
      if (response.status === 200 && response.data.isSuccess) {
        setSuccess({
          heading: 'Success!',
          message: 'Terms of Service accepted and user updated.',
        });
      }
    } catch (err) {
      strogging.log('Error accepting Terms of Service', err);
      setError({ message: `Error accepting Terms of Service: ${err}` });
    } finally {
      setLoading(false);
    }
  };

  return {
    acceptTos,
    loading,
    success,
    error,
  };
};

export const useAdminDeleteUser = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorMessage | null>();
  const [success, setSuccess] = useState<SuccessMessage | null>();

  const deleteUser = async (userId: string) => {
    try {
      setLoading(true);
      const response = await axios.delete(`/api/admin/user/${userId}/delete`);
      if (response.status === 200 && response.data) {
        setSuccess({
          heading: 'Success!',
          message: 'User has been deleted. Redirecting...',
        });
        return response.data;
      }
    } catch (err) {
      strogging.log(`Error deleting user ${userId} `, err);
      setError({ message: `Error deleting user: ${err}` });
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteUser,
    loading,
    success,
    error,
  };
};

export const useGetCustomClaims = () => {
  const [customClaims, setCustomClaims] = useState<Record<
    string,
    unknown
  > | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorMessage | null>();

  const getCustomClaims = async (uid: string) => {
    setError(null);
    setLoading(true);
    try {
      const response = await axios.get(`/api/impersonate/${uid}`);
      if (response.status === 200 && response.data) {
        setCustomClaims(response.data);
      }
    } catch (err) {
      setError({
        message: `Error getting custom claims for user ${uid}: ${err}`,
      });
      strogging.log(`Error getting custom claims for user ${uid} `, err);
    } finally {
      setLoading(false);
    }
  };

  return {
    customClaims,
    error,
    getCustomClaims,
    loading,
  };
};

export const useAdminUpdatePhoneNumber = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorMessage | null>();
  const [success, setSuccess] = useState<SuccessMessage | null>();
  const updatePhoneNumber = async (userId: string, phoneNumber: string) => {
    try {
      setLoading(true);
      setSuccess(null);
      setError(null);
      const response = await axios.put(
        `/api/admin/user/${userId}/update_phone_number`,
        {
          phone_number: phoneNumber,
        }
      );
      if (response.status === 200 && response.data.isSuccess) {
        setSuccess({
          heading: 'Success!',
          message: 'Phone number updated',
        });
      }
    } catch (err) {
      const message = `Error updating phone number: ${err}`;
      strogging.log(message);
      setError({ message: message });
    } finally {
      setLoading(false);
    }
  };

  return {
    updatePhoneNumber,
    loading,
    success,
    error,
  };
};
